// Graph data model types for Relationship Manager

export type NodeKind = 
  | 'Device' 
  | 'Sensor'
  | 'DeviceModel'
  | 'Decoder' 
  | 'Template' 
  | 'Attribute'
  | 'Topic' 
  | 'Service' 
  | 'ApiRoute' 
  | 'App' 
  | 'PimItem' 
  | 'Asset' 
  | 'Profile' 
  | 'Agent'
  | 'Policy'
  | 'Brand'
  | 'Manufacturer'
  | 'LegalEntity'
  | 'Cooperative'
  | 'CloseCorporation'
  | 'NonProfit'
  | 'GovernmentEntity'
  | 'Estate'
  | 'Contact'
  | 'Individual'
  | 'Component'
  | 'Engine'
  | 'IKUCategory'
  | 'KindSpine'
  | 'ReferenceCategory';

export type NodeStatus = 'ok' | 'warn' | 'error' | 'unknown';

export type EdgeType = 
  | 'decodes_with'
  | 'publishes'
  | 'consumes'
  | 'exposes_as'
  | 'validates_with'
  | 'has_attribute'
  | 'stores_in'
  | 'belongs_to'
  | 'measures'
  | 'located_at'
  | 'compatible_with'
  | 'enriches'
  | 'governed_by'
  | 'bundles'
  | 'uses'
  | 'emits'
  | 'contains'
  | 'part_of'
  | 'component_of'
  | 'manufactured_by'
  | 'branded_by'
  | 'owned_by'
  | 'operates'
  | 'fitted_with'
  | 'telematics'
  | 'model_of'
  | 'source_document'
  | 'works_for'
  | 'director_of'
  | 'trustee_of'
  | 'member_of'
  | 'exhibits_at'
  | 'treats'
  | 'subsidiary_of'
  | 'owns_brand'
  | 'parent_brand_of'
  | 'sub_brand_of'
  | 'branded_as'
  | 'brand_for'
  | 'distributes_for'
  | 'distributed_by'
  | 'has_component'
  | 'manufactures'
  | 'targets'
  | 'targeted_by'
  | 'registered_for'
  | 'planted_as'
  | 'harvested_as'
  | 'produces'
  | 'generates_data'
  | 'feeds_on'
  | 'complies_with'
  | 'deployed_at'
  | 'monitored_by'
  | 'assigned_to'
  | 'has_assigned'
  | 'sold_by'
  | 'contracted_to'
  | 'applied_to'
  | 'serviced_by'
  | 'documented_by'
  | 'relationship_type_definition'
  | string;

export type EdgeStatus = 'observed' | 'desired' | 'diverged';

export interface GraphNode {
  id: string; // e.g., "dev:device:collar-0001"
  kind: NodeKind;
  label: string;
  status: NodeStatus;
  metrics?: {
    lastSeen?: string;
    rqps?: number;
    p95?: number;
    [key: string]: any;
  };
  tags: string[]; // e.g., ["tenant:dev", "type:livestock_collar"]
  props: Record<string, any>; // kind-specific properties
  // React Flow specific
  position?: { x: number; y: number };
  data?: any;
  type?: string;
}

export interface GraphEdge {
  id: string; // e.g., "edge:dev:collar-0001->dec_v2"
  type: EdgeType;
  from: string; // source node id
  to: string; // target node id
  props: Record<string, any>;
  status: EdgeStatus;
  // React Flow specific
  source?: string;
  target?: string;
  animated?: boolean;
  style?: Record<string, any>;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

// Planning and ChangeSet types
export interface PlanOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  domain: string; // e.g., 'IoTDevice', 'ApiService', 'RelationshipEdge'
  resource: string;
  description: string;
  impact: 'low' | 'medium' | 'high';
  properties?: Record<string, any>;
}

export interface PlanPolicy {
  id: string;
  type: 'approval_required' | 'version_bump' | 'downtime_warning' | 'tenant_isolation';
  message: string;
  severity: 'info' | 'warning' | 'error';
}

export interface PlanResponse {
  draftChangeSet: {
    id: string;
    operations: PlanOperation[];
    metadata: Record<string, any>;
  };
  policy: PlanPolicy[];
  impact: {
    summary: string;
    affectedServices: string[];
    downtime: boolean;
    dataLoss: boolean;
  };
  hints: string[];
}

// Search and filtering
export interface GraphFilters {
  kinds?: NodeKind[];
  statuses?: NodeStatus[];
  tags?: string[];
  relations?: EdgeType[];
  search?: string;
}

export interface GraphScope {
  scope: 'tenant' | 'node';
  rootId?: string;
  depth?: number;
  filters?: GraphFilters;
}

// Lens presets
export interface GraphLens {
  id: string;
  name: string;
  description: string;
  scope: GraphScope;
  layout?: 'force' | 'hierarchical' | 'grid';
  icon?: string;
}

// Planning modes
export type PlanningMode = 'domain' | 'graph';

// Node connection constraints
export const ALLOWED_CONNECTIONS: Record<NodeKind, Array<{ target: NodeKind; edgeType: EdgeType }>> = {
  Device: [
    { target: 'Decoder', edgeType: 'decodes_with' },
    { target: 'Template', edgeType: 'validates_with' },
    { target: 'Template', edgeType: 'measures' },
    { target: 'Template', edgeType: 'belongs_to' },
    { target: 'PimItem', edgeType: 'measures' },
    { target: 'DeviceModel', edgeType: 'belongs_to' },
    { target: 'Policy', edgeType: 'governed_by' },
    { target: 'Sensor', edgeType: 'contains' }
  ],
  Sensor: [
    { target: 'Template', edgeType: 'validates_with' },
    { target: 'DeviceModel', edgeType: 'belongs_to' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  DeviceModel: [
    { target: 'Template', edgeType: 'validates_with' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Decoder: [
    { target: 'Template', edgeType: 'emits' },
    { target: 'Template', edgeType: 'validates_with' },
    { target: 'Topic', edgeType: 'publishes' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Template: [
    { target: 'Attribute', edgeType: 'has_attribute' },
    { target: 'Brand', edgeType: 'branded_by' },
    { target: 'Manufacturer', edgeType: 'manufactured_by' },
    { target: 'LegalEntity', edgeType: 'owned_by' },
    { target: 'Template', edgeType: 'component_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Topic: [
    { target: 'Service', edgeType: 'consumes' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Service: [
    { target: 'Topic', edgeType: 'consumes' },
    { target: 'Topic', edgeType: 'publishes' },
    { target: 'ApiRoute', edgeType: 'exposes_as' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  ApiRoute: [
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  App: [
    { target: 'Service', edgeType: 'bundles' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  PimItem: [
    { target: 'Asset', edgeType: 'stores_in' },
    { target: 'Asset', edgeType: 'uses' },
    { target: 'Template', edgeType: 'validates_with' },
    { target: 'PimItem', edgeType: 'contains' },
    { target: 'PimItem', edgeType: 'part_of' },
    { target: 'Device', edgeType: 'measures' },
    { target: 'Component', edgeType: 'contains' },
    { target: 'Engine', edgeType: 'contains' },
    { target: 'Brand', edgeType: 'branded_by' },
    { target: 'Manufacturer', edgeType: 'manufactured_by' },
    { target: 'LegalEntity', edgeType: 'owned_by' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Asset: [
    { target: 'Template', edgeType: 'validates_with' },
    { target: 'Sensor', edgeType: 'belongs_to' },
    { target: 'DeviceModel', edgeType: 'belongs_to' },
    { target: 'LegalEntity', edgeType: 'owned_by' },
    { target: 'Device', edgeType: 'fitted_with' },
    { target: 'Device', edgeType: 'telematics' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Attribute: [
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Profile: [
    { target: 'Device', edgeType: 'compatible_with' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Agent: [
    { target: 'Template', edgeType: 'enriches' },
    { target: 'PimItem', edgeType: 'enriches' },
    { target: 'Service', edgeType: 'uses' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Brand: [
    { target: 'LegalEntity', edgeType: 'owned_by' },
    { target: 'Brand', edgeType: 'sub_brand_of' },
    { target: 'Brand', edgeType: 'parent_brand_of' },
    { target: 'PimItem', edgeType: 'brand_for' },
    { target: 'LegalEntity', edgeType: 'distributed_by' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Manufacturer: [
    { target: 'PimItem', edgeType: 'manufactures' },
    { target: 'Brand', edgeType: 'owns_brand' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  LegalEntity: [
    { target: 'LegalEntity', edgeType: 'subsidiary_of' },
    { target: 'Brand', edgeType: 'owns_brand' },
    { target: 'Brand', edgeType: 'distributes_for' },
    { target: 'PimItem', edgeType: 'manufactures' },
    { target: 'Asset', edgeType: 'owned_by' },
    { target: 'Asset', edgeType: 'operates' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Cooperative: [
    { target: 'LegalEntity', edgeType: 'member_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  CloseCorporation: [
    { target: 'LegalEntity', edgeType: 'subsidiary_of' },
    { target: 'Brand', edgeType: 'owns_brand' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  NonProfit: [
    { target: 'LegalEntity', edgeType: 'subsidiary_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  GovernmentEntity: [
    { target: 'LegalEntity', edgeType: 'subsidiary_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Estate: [
    { target: 'LegalEntity', edgeType: 'subsidiary_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Contact: [
    { target: 'LegalEntity', edgeType: 'works_for' },
    { target: 'LegalEntity', edgeType: 'director_of' },
    { target: 'LegalEntity', edgeType: 'trustee_of' },
    { target: 'LegalEntity', edgeType: 'member_of' },
    { target: 'App', edgeType: 'uses' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Individual: [
    { target: 'LegalEntity', edgeType: 'works_for' },
    { target: 'LegalEntity', edgeType: 'director_of' },
    { target: 'LegalEntity', edgeType: 'trustee_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Component: [
    { target: 'Template', edgeType: 'component_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Engine: [
    { target: 'Template', edgeType: 'component_of' },
    { target: 'Policy', edgeType: 'governed_by' }
  ],
  Policy: [],
  IKUCategory: [],
  KindSpine: [],
  ReferenceCategory: []
};

// Node colors and styling
export const NODE_STYLES: Record<NodeKind, { color: string; bgColor: string; icon: string }> = {
  Device: { color: '#10B981', bgColor: '#ECFDF5', icon: '📱' },
  Sensor: { color: '#16A34A', bgColor: '#ECFDF5', icon: '🛰️' },
  DeviceModel: { color: '#22C55E', bgColor: '#F0FDF4', icon: '🏷️' },
  Decoder: { color: '#8B5CF6', bgColor: '#F3E8FF', icon: '🔧' },
  Template: { color: '#F59E0B', bgColor: '#FFFBEB', icon: '📋' },
  Attribute: { color: '#FACC15', bgColor: '#FEFCE8', icon: '🔤' },
  Topic: { color: '#EF4444', bgColor: '#FEF2F2', icon: '📡' },
  Service: { color: '#3B82F6', bgColor: '#EFF6FF', icon: '⚙️' },
  ApiRoute: { color: '#06B6D4', bgColor: '#ECFEFF', icon: '🛣️' },
  App: { color: '#84CC16', bgColor: '#F7FEE7', icon: '📦' },
  PimItem: { color: '#F97316', bgColor: '#FFF7ED', icon: '📊' },
  Asset: { color: '#EC4899', bgColor: '#FDF2F8', icon: '💾' },
  Profile: { color: '#6366F1', bgColor: '#EEF2FF', icon: '👤' },
  Agent: { color: '#A855F7', bgColor: '#FAF5FF', icon: '🤖' },
  Policy: { color: '#64748B', bgColor: '#F8FAFC', icon: '🛡️' },
  Brand: { color: '#F97316', bgColor: '#FFF7ED', icon: '🏷️' },
  Manufacturer: { color: '#0EA5E9', bgColor: '#E0F2FE', icon: '🏭' },
  LegalEntity: { color: '#1D4ED8', bgColor: '#DBEAFE', icon: '🏢' },
  Cooperative: { color: '#0369A1', bgColor: '#E0F2FE', icon: '🤝' },
  CloseCorporation: { color: '#1E40AF', bgColor: '#DBEAFE', icon: '🏛️' },
  NonProfit: { color: '#7C3AED', bgColor: '#EDE9FE', icon: '💜' },
  GovernmentEntity: { color: '#B45309', bgColor: '#FEF3C7', icon: '🏛️' },
  Estate: { color: '#78716C', bgColor: '#F5F5F4', icon: '📜' },
  Contact: { color: '#059669', bgColor: '#D1FAE5', icon: '👤' },
  Individual: { color: '#0891B2', bgColor: '#CFFAFE', icon: '🧑' },
  Component: { color: '#F43F5E', bgColor: '#FFE4E6', icon: '🧩' },
  Engine: { color: '#DC2626', bgColor: '#FEE2E2', icon: '🛠️' },
  IKUCategory: { color: '#6366F1', bgColor: '#EEF2FF', icon: '📂' },
  KindSpine: { color: '#6366F1', bgColor: '#EEF2FF', icon: '📂' },
  ReferenceCategory: { color: '#7C3AED', bgColor: '#F5F3FF', icon: '📖' }
};

// Edge styles
export const EDGE_STYLES: Record<EdgeStatus, { strokeDasharray?: string; stroke: string }> = {
  observed: { stroke: '#10B981' },
  desired: { strokeDasharray: '5 5', stroke: '#F59E0B' },
  diverged: { stroke: '#EF4444' }
};
